/**
 * Fractional indexing — z-order as a string, so inserting between two objects renumbers nothing.
 *
 * PLAN.md §4.4.2 and RESEARCH.md §5 settle this: Figma, Excalidraw and tldraw all independently
 * arrived at ordered strings rather than integers, because an array index cannot be shared. Two
 * people who both "bring to front" against integer z produce the same number and then have to
 * agree who renumbers the rest of the sheet. A key between two keys is a local decision that can
 * never conflict with anyone else's local decision, which is the whole point.
 *
 * ── The shape of a key ─────────────────────────────────────────────────────────────────────
 *
 * A key is an **integer part** followed by a **fraction part**, both in base 62, in an alphabet
 * whose ASCII order is its numeric order — so a plain `a < b` string comparison IS the paint order
 * and a sort needs no parsing at all.
 *
 * The integer part is length-prefixed by its own first character: heads `a`…`z` are positive and
 * carry 1…26 digits, heads `Z`…`A` are negative and carry 1…26. `a0` is zero and the first key on
 * an empty sheet.
 *
 * That header is not decoration, and it was arrived at by measuring the naive version. Without it,
 * appending has to halve the gap to the top of the range every time, and a thousand strokes — one
 * evening's working — produced keys **200 characters long**, several times the size of the objects
 * they were ordering. With it, appending is an integer increment: one character per 62 objects,
 * two per 3,844, and a thousand strokes end at four. `tests/sortkey.test.ts` measures both.
 *
 * One invariant makes the midpoint terminate: **a fraction never ends in the zero digit.** A
 * trailing zero is a shorter equal value, and the midpoint of two equal values does not exist.
 *
 * `SceneObject.z` stays a number, because the renderer and the existing scene code want one. The
 * key is authoritative in sync-land and `z` is derived from it on the way out — see `toScene` in
 * merge.ts.
 */

/** 62 characters, ASCII-ascending, so lexicographic order is numeric order. */
export const DIGITS = '0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz';

const ZERO = DIGITS[0]!;
const TOP = DIGITS[DIGITS.length - 1]!;
const BASE = DIGITS.length;

/** Zero, and the key an empty sheet's first object gets. */
export const ZERO_KEY = 'a0';

/** The floor. Nothing may be generated below it, so `keyBefore` has somewhere to refuse. */
const SMALLEST_INTEGER = `A${ZERO.repeat(26)}`;

/** How many characters the integer part is, read off its head. */
function integerLength(head: string): number {
  if (head >= 'a' && head <= 'z') return head.charCodeAt(0) - 'a'.charCodeAt(0) + 2;
  if (head >= 'A' && head <= 'Z') return 'Z'.charCodeAt(0) - head.charCodeAt(0) + 2;
  throw new Error(`not a sort key head: ${JSON.stringify(head)}`);
}

const integerPart = (key: string): string => {
  const length = integerLength(key[0]!);
  if (length > key.length) throw new Error(`sort key is too short: ${JSON.stringify(key)}`);
  return key.slice(0, length);
};

const validateInteger = (part: string): void => {
  if (part.length !== integerLength(part[0]!)) {
    throw new Error(`not an integer part: ${JSON.stringify(part)}`);
  }
};

/** A well-formed key: a legal integer part, and a fraction that does not end in the zero digit. */
export function isKey(value: unknown): value is string {
  if (typeof value !== 'string' || value.length === 0) return false;
  for (const char of value) if (!DIGITS.includes(char)) return false;
  if (value === SMALLEST_INTEGER) return false;
  try {
    const integer = integerPart(value);
    validateInteger(integer);
    return !value.slice(integer.length).endsWith(ZERO);
  } catch { return false; }
}

const assertKey = (value: string | null, name: string): void => {
  if (value !== null && !isKey(value)) throw new Error(`${name} is not a sort key: ${JSON.stringify(value)}`);
};

// ── The integer part ───────────────────────────────────────────────────────────────────────

/** `null` when there is no next integer — the top of the representable range. */
function incrementInteger(part: string): string | null {
  validateInteger(part);
  const [head, ...digits] = part.split('');
  let carry = true;
  for (let i = digits.length - 1; carry && i >= 0; i--) {
    const next = DIGITS.indexOf(digits[i]!) + 1;
    if (next === BASE) digits[i] = ZERO;
    else { digits[i] = DIGITS[next]!; carry = false; }
  }
  if (!carry) return head! + digits.join('');
  // The digits all rolled over, so the integer needs a wider head — which is exactly what makes
  // the key length grow logarithmically rather than linearly.
  if (head === 'Z') return `a${ZERO}`;
  if (head === 'z') return null;
  const wider = String.fromCharCode(head!.charCodeAt(0) + 1);
  if (wider > 'a') digits.push(ZERO); else digits.pop();
  return wider + digits.join('');
}

/** `null` when there is no previous integer — the bottom of the representable range. */
function decrementInteger(part: string): string | null {
  validateInteger(part);
  const [head, ...digits] = part.split('');
  let borrow = true;
  for (let i = digits.length - 1; borrow && i >= 0; i--) {
    const next = DIGITS.indexOf(digits[i]!) - 1;
    if (next === -1) digits[i] = TOP;
    else { digits[i] = DIGITS[next]!; borrow = false; }
  }
  if (!borrow) return head! + digits.join('');
  if (head === 'a') return `Z${TOP}`;
  if (head === 'A') return null;
  const wider = String.fromCharCode(head!.charCodeAt(0) - 1);
  if (wider < 'Z') digits.push(TOP); else digits.pop();
  return wider + digits.join('');
}

// ── The fraction part ──────────────────────────────────────────────────────────────────────

/**
 * The midpoint of two fractions, both exclusive.
 *
 * `''` means zero and `null` for the upper bound means one, which is how "before everything" and
 * "after everything" are expressed without a special case.
 */
function midpoint(lower: string, upper: string | null): string {
  if (upper !== null && lower >= upper) {
    throw new Error(`sort keys out of order: ${JSON.stringify(lower)} >= ${JSON.stringify(upper)}`);
  }
  if (lower.endsWith(ZERO) || (upper !== null && upper.endsWith(ZERO))) {
    throw new Error('a sort key fraction may not end in the zero digit');
  }

  // A shared prefix is carried through untouched; the interesting arithmetic is what follows it.
  if (upper !== null) {
    let shared = 0;
    while ((lower[shared] ?? ZERO) === upper[shared]) shared += 1;
    if (shared > 0) return upper.slice(0, shared) + midpoint(lower.slice(shared), upper.slice(shared));
  }

  const low = lower === '' ? 0 : DIGITS.indexOf(lower[0]!);
  const high = upper === null || upper === '' ? BASE : DIGITS.indexOf(upper[0]!);

  // Room for a digit strictly between the two: one character is enough and the key stays short.
  if (high - low > 1) return DIGITS[Math.round(0.5 * (low + high))]!;

  // Consecutive first digits. Descend into whichever side still has room below it.
  if (upper !== null && upper.length > 1) return upper.slice(0, 1);
  return DIGITS[low]! + midpoint(lower.slice(1), null);
}

// ── The public surface ─────────────────────────────────────────────────────────────────────

/**
 * A key strictly between `before` and `after`. `null` on either side means "the end".
 *
 * Appending and prepending are integer steps, so they cost a character per 62 objects. Only an
 * insert BETWEEN two adjacent keys has to descend into the fraction, which is the rare case and
 * the one that can afford to be longer.
 */
export function keyBetween(before: string | null, after: string | null): string {
  assertKey(before, 'before');
  assertKey(after, 'after');
  if (before !== null && after !== null && before >= after) {
    throw new Error(`sort keys out of order: ${JSON.stringify(before)} >= ${JSON.stringify(after)}`);
  }

  if (before === null) {
    if (after === null) return ZERO_KEY;
    const integer = integerPart(after);
    const fraction = after.slice(integer.length);
    if (integer === SMALLEST_INTEGER) return integer + midpoint('', fraction);
    if (integer < after) return integer;
    const lower = decrementInteger(integer);
    if (lower === null) throw new Error('sort keys have run out below');
    return lower;
  }

  if (after === null) {
    const integer = integerPart(before);
    const fraction = before.slice(integer.length);
    const higher = incrementInteger(integer);
    return higher === null ? integer + midpoint(fraction, null) : higher;
  }

  const integerBefore = integerPart(before);
  const fractionBefore = before.slice(integerBefore.length);
  const integerAfter = integerPart(after);
  const fractionAfter = after.slice(integerAfter.length);
  if (integerBefore === integerAfter) return integerBefore + midpoint(fractionBefore, fractionAfter);

  const higher = incrementInteger(integerBefore);
  if (higher === null) throw new Error('sort keys have run out above');
  if (higher < after) return higher;
  return integerBefore + midpoint(fractionBefore, null);
}

export const keyAfter = (last: string | null): string => keyBetween(last, null);

export const keyBefore = (first: string | null): string => keyBetween(null, first);

/** `count` keys in ascending order between the two bounds — what a paste of six objects needs. */
export function keysBetween(before: string | null, after: string | null, count: number): string[] {
  if (count <= 0) return [];
  // Appending is a chain of integer increments; halving would be correct and needlessly long.
  if (after === null) {
    const keys: string[] = [];
    let last = before;
    for (let i = 0; i < count; i++) { last = keyBetween(last, null); keys.push(last); }
    return keys;
  }
  if (count === 1) return [keyBetween(before, after)];
  // Between two real bounds, halving keeps every key short; filling left to right would make the
  // last one long.
  const middle = keyBetween(before, after);
  const left = Math.floor((count - 1) / 2);
  return [
    ...keysBetween(before, middle, left),
    middle,
    ...keysBetween(middle, after, count - 1 - left),
  ];
}

/** Ascending keys for a list that has an order but no keys yet — the first sync of a local sheet. */
export const initialKeys = (count: number): string[] => keysBetween(null, null, count);

/** Sort by key, then by id, so the order is total even if two keys somehow collide. */
export const byKey = <T extends { index: string; id: string }>(a: T, b: T): number =>
  a.index === b.index ? (a.id < b.id ? -1 : a.id > b.id ? 1 : 0) : a.index < b.index ? -1 : 1;
